"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GuessActionScene = void 0;
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
const pixi_js_1 = require("pixi.js");
const Global_1 = require("../Global");
const settings_1 = require("../config/settings");
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const MenuButton_1 = require("../blocks/MenuButton");
const ActionScene_1 = require("./ActionScene");
const lodash_1 = __importDefault(require("lodash"));
const gsap_core_1 = require("gsap/gsap-core");
const GuessOpponent_1 = require("../blocks/GuessOpponent");
class GuessActionScene extends ActionScene_1.ActionScene {
    constructor(app, sceneManager, gameManager) {
        super(app, sceneManager, gameManager);
        this.duration = 0.007;
        this.buttons = new Map();
        this.hasguessed = null;
        this.myself = this.makeOpponent(gameManager.playerManager.pkey);
        this.addChild(this.userpanel);
        this.canvas.stroke = (0, Global_1.randomColor)();
        this.auto = this.canvas.addDrawing(this.round.drawing, this.duration);
        this.totaltime = this.auto.timeline.duration();
        this.auto.timeline.pause(0);
        this.auto.blurFilter.blur = (0, Global_1.px)(0.4);
        this.guessMenu = this.makeGuessMenu();
        // TODO: This is unreliable, sometimes skips get notified because the timeout is always 0
        this.skiptimer = setTimeout(() => {
            this.gameManager.notifySkip();
        }, this.cuttime - Date.now());
        this.subs.add(this.gameManager.roomStream.guess$.subscribe((glabel) => __awaiter(this, void 0, void 0, function* () {
            if ((glabel === null || glabel === void 0 ? void 0 : glabel.label) && this.buttons.has(glabel.label)) {
                const btn = this.buttons.get(glabel.label);
                if (this.gameManager.playerManager.pkey === glabel.pkey) {
                    yield this.highlightButton(btn, this.myself, false);
                }
                else if (this.opponents[glabel.pkey]) {
                    // Was an opponent wrong guess!
                    yield this.highlightButton(btn, (this.opponents[glabel.pkey]), false);
                }
            }
        })));
    }
    finish(winner = null) {
        const _super = Object.create(null, {
            finish: { get: () => super.finish }
        });
        return __awaiter(this, void 0, void 0, function* () {
            this.disable();
            const iwon = winner === this.gameManager.playerManager.pkey;
            if (iwon && this.hasguessed && this.buttons.has(this.hasguessed)) {
                yield this.highlightButton(this.buttons.get(this.hasguessed), this.myself, true);
            }
            yield _super.finish.call(this, winner);
        });
    }
    addOpponent(player) {
        const _super = Object.create(null, {
            addOpponent: { get: () => super.addOpponent }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const opponent = yield _super.addOpponent.call(this, player);
            if (!opponent.visible) {
                opponent.position.y = (_a = this.canvas) === null || _a === void 0 ? void 0 : _a.y;
                opponent.rotation = 0;
                opponent.visible = true;
                try {
                    yield this.promisedTimeline(gsap_core_1.gsap.timeline()
                        .to(opponent, {
                        pixi: {
                            positionY: 0
                        },
                        ease: 'power3.out',
                        duration: 0.5
                    }));
                }
                catch (e) { }
            }
            this.layout();
            return opponent;
        });
    }
    dropOpponent(player) {
        const _super = Object.create(null, {
            dropOpponent: { get: () => super.dropOpponent }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const opponent = yield _super.dropOpponent.call(this, player);
            if (opponent) {
                opponent.visible = true;
                try {
                    yield this.promisedTimeline(gsap_core_1.gsap.timeline()
                        .to(opponent, {
                        yoyo: true,
                        keyframes: [
                            { pixi: { rotation: 16 } },
                            { pixi: { rotation: 8 } },
                            { pixi: { rotation: 11 } }
                        ],
                        duration: 0.5
                    })
                        .to(opponent, {
                        pixi: {
                            positionY: (_a = this.canvas) === null || _a === void 0 ? void 0 : _a.y
                        },
                        ease: 'power3.in',
                        duration: 0.5
                    }));
                }
                catch (e) { }
                opponent.visible = false;
            }
            return opponent;
        });
    }
    chooseGuess(item) {
        if (!this.hasguessed) {
            this.hasguessed = item;
            this.disable();
            this.gameManager.guess(item);
        }
    }
    highlightButton(btn, user, correct = true) {
        return __awaiter(this, void 0, void 0, function* () {
            btn.color = correct ? settings_1.Settings.successColor : settings_1.Settings.errorColor;
            btn.textColor = settings_1.Settings.whiteColor;
            btn.layout();
            yield user.inOverlay(btn.color);
        });
    }
    disable() {
        this.guessMenu.children.forEach((btn) => {
            btn.enabled = false;
        });
        this.buttons.forEach((btn) => {
            btn.alpha = 0.95;
        });
    }
    makeGuessMenu() {
        var _a;
        const container = new pixi_js_1.Container();
        if (((_a = this.round.guesses) === null || _a === void 0 ? void 0 : _a.length) > 0) {
            this.round.guesses.forEach((t) => {
                const button = new MenuButton_1.MenuButton(this.app, (0, Global_1.category)(t, false), settings_1.Settings.whiteColor, this.isLandscapeBox ? 186 : 200, 54, 10);
                this.buttons.set(t, button);
                const btn = button.asButton(0.01);
                this.sigs.push(btn.onPress.connect(() => {
                    this.chooseGuess(t);
                }));
                container.addChild(btn);
            });
        }
        return this.addChild(container);
    }
    get cuttime() {
        if (this.auto) {
            const cutoff = (Math.ceil(this.totaltime) * 1000) + config_json_1.default.roundguesstime;
            return (new Date(Math.floor(this.round.time / 1000) + this.round.start + cutoff)).getTime();
        }
        return Date.now();
    }
    get canvasoptions() {
        const p = this.isLandscapeBox ? 120 : 10;
        const w = this.app.screen.width / (0, Global_1.px)() - p;
        return {
            strokeWidth: this.overSmallSize ? 8 : 6,
            width: Math.min(w, settings_1.Settings.maxWidth - p),
            height: this.app.screen.height / (0, Global_1.px)() / 1.8,
            disabled: true
        };
    }
    init() {
        const _super = Object.create(null, {
            init: { get: () => super.init }
        });
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            yield _super.init.call(this);
            (_a = this.auto) === null || _a === void 0 ? void 0 : _a.timeline.play();
        });
    }
    get canvas() {
        return this.myCanvas;
    }
    destroy(_options) {
        clearTimeout(this.skiptimer);
        super.destroy(_options);
    }
    get isValid() {
        return super.isValid && !!this.round.drawing;
    }
    makeOpponent(pkey) {
        if (!this.userpanel) {
            this.userpanel = new pixi_js_1.Container();
        }
        const user = new GuessOpponent_1.GuessOpponent(this.app, this.gameManager, pkey);
        user.scale.set(this.useBigger ? 1 : 0.88);
        return this.userpanel.addChild(user);
    }
    get useBigger() {
        return this.overSmallSize && this.app.screen.width >= (0, Global_1.px)(450);
    }
    layout() {
        var _a, _b, _c, _d, _e, _f, _g;
        super.layout();
        (_a = this.canvas) === null || _a === void 0 ? void 0 : _a.position.set(this.app.screen.width - this.myCanvas.width / 2, (0, Global_1.px)(this.isLandscapeBox ? 80 : 130));
        (_b = this.rightStatus) === null || _b === void 0 ? void 0 : _b.position.set(this.center.x + this.myCanvas.width / 2 - (0, Global_1.px)(19), this.myCanvas.y + (0, Global_1.px)(14));
        if (this.leftStatus) {
            this.leftStatus.visible = false;
        }
        const people = [this.myself].concat(this.opponents ? (lodash_1.default.values(this.opponents)) : []);
        const width = (0, Global_1.px)(this.myself.banner.w);
        const height = (0, Global_1.px)(this.myself.banner.h);
        if (this.userpanel) {
            let total = 0;
            for (let i = 0; i < config_json_1.default.roomsize; i++) {
                const op = people[i];
                const s = this.useBigger ? 1 : 0.88;
                op === null || op === void 0 ? void 0 : op.scale.set(s);
                const mx = (0, Global_1.px)(8 * i) + (i * width * s);
                op === null || op === void 0 ? void 0 : op.position.set(mx, 0);
                total += mx;
            }
            this.userpanel.zIndex = 0;
            this.userpanel.position.set(this.center.x - total / 2, ((_c = this.myCanvas) === null || _c === void 0 ? void 0 : _c.y) - height - (0, Global_1.px)(16));
        }
        let cols = 0;
        (_d = this.guessMenu) === null || _d === void 0 ? void 0 : _d.children.forEach((btn, i) => {
            btn.pivot.set(btn.width / 2, btn.height / 2);
            if (this.isLandscape) {
                const y = btn.height / 2;
                cols += btn.width / 2;
                btn.position.set(cols, y);
                cols += (btn.width / 2 + (0, Global_1.px)(5));
            }
            else {
                const y = ((btn.height + (0, Global_1.px)(8)) * cols) + btn.height / 2;
                const c = btn.width / 2;
                if (i % 2) {
                    btn.position.set(c + btn.width + (0, Global_1.px)(8), y);
                    cols++;
                }
                else {
                    btn.position.set(c, y);
                }
            }
        });
        (_e = this.guessMenu) === null || _e === void 0 ? void 0 : _e.scale.set(this.useBigger ? 1.0 : 0.8);
        (_f = this.guessMenu) === null || _f === void 0 ? void 0 : _f.position.set(this.center.x - ((_g = this.guessMenu) === null || _g === void 0 ? void 0 : _g.width) / 2, this.canvas.y + this.canvas.height + (0, Global_1.px)(16));
    }
}
exports.GuessActionScene = GuessActionScene;
